import * as Sentry from '@sentry/react';
import type { TelemetryAdapter, TelemetryUser, ErrorContext, MessageContext } from '../types';

export interface SentryInitConfig {
  dsn: string;
  environment: string;
  release?: string;
}

export function initSentry(config: SentryInitConfig): void {
  Sentry.init({
    dsn: config.dsn,
    environment: config.environment,
    release: config.release,
    tracesSampleRate: 0,
    integrations: [],
  });
}

export function createSentryAdapter(): TelemetryAdapter {
  return {
    name: 'sentry',

    identify(user: TelemetryUser) {
      Sentry.setUser({
        id: user.id,
        email: user.email,
        username: user.name,
      });
      if (user.tenant) Sentry.setTag('tenant', user.tenant);
      if (user.orgName) Sentry.setTag('org', user.orgName);
    },

    captureError(error: Error, context?: ErrorContext) {
      Sentry.captureException(error, {
        ...(context?.tags && { tags: context.tags }),
        ...(context?.contexts && { contexts: context.contexts }),
        ...(context?.level && { level: context.level }),
        ...(context?.fingerprint && { fingerprint: context.fingerprint }),
      });
    },

    captureMessage(message: string, context?: MessageContext) {
      Sentry.captureMessage(message, {
        level: context?.level ?? 'info',
        ...(context?.tags && { tags: context.tags }),
        ...(context?.contexts && { contexts: context.contexts }),
      });
    },

    addBreadcrumb(
      category: string,
      message: string,
      data?: Record<string, unknown>,
      level?: 'info' | 'warning' | 'error'
    ) {
      Sentry.addBreadcrumb({
        category,
        message,
        data,
        level: level ?? 'info',
      });
    },
  };
}
